/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box, Grid, List, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ApiError, DocumentContract, DocumentSigner } from "@interfaces/response";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import ListDocuments from "@components/ListDocuments";
import { PERSON_CODES_LEGAL_REPRESENTATIVE, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { CheckListSignatureDocument } from "@interfaces/catalogs";
import { formatPhoneNumber, validateStructureCheckListSignatureDocument } from "@helpers/funciones";
import ButtonFirma from "./ButtonFirma";

const DEFAULT_CONFIG_PFAE: CheckListSignatureDocument[] = [
  {
    order: 1,
    title: "Documentos Contractuales",
    documents: ["Carátula", "Contrato", "Tabla de amortización", "Autorización de domiciliación"],
  },
  {
    order: 2,
    title: "Pagaré",
    documents: [],
  },
  {
    order: 3,
    title: "Expediente KYC y anexos",
    documents: ["Carta de obligaciones", "Carta de Autorización Descuentos", "Solicitud de financiamientos"],
  },
];

const DEFAULT_CONFIG_PM: CheckListSignatureDocument[] = [
  {
    order: 1,
    title: "Documentos Contractuales",
    documents: ["Carátula", "Contrato", "Tabla de amortización", "Autorización de domiciliación"],
  },
  {
    order: 2,
    title: "Pagaré",
    documents: [],
  },
  {
    order: 3,
    title: "Expediente KYC y anexos",
    documents: [
      "Carta de obligaciones",
      "Carta de Autorización Descuentos",
      "Solicitud de financiamientos",
      "Anexo 2 - Propietario real",
    ],
  },
];

interface FirmaLegalarioProps {
  documents: DocumentContract[];
  checklistDocuments: CheckListSignatureDocument[];
}

const FirmaLegalario: React.FC<FirmaLegalarioProps> = ({ documents, checklistDocuments }) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);

  const [checklistDocumentsToSign, setChecklistDocumentsToSign] = useState<CheckListSignatureDocument[]>([]);
  const [legalRepresentatives, setLegalRepresentatives] = useState<DocumentSigner[]>([]);

  const handleSignLegalRepresentative = async (signer: DocumentSigner) => {
    try {
      if (!signer || !signer.id) {
        throw new Error("Signer ID is not defined");
      }

      const legalarioConfig = interfaceConfig.firma?.legalario_config;
      if (!legalarioConfig) {
        throw new Error("Legalario config is not defined");
      }

      const legalarioParams: LegalarioSParams = {
        organizationId: legalarioConfig?.organization_id,
        apiKey: legalarioConfig?.api_key,
        env: import.meta.env.VITE_ENV === "PROD" ? "PRODUCTION" : "SANDBOX",
      };

      const legalario = new LegalarioSDK(legalarioParams);

      const modules: LegalarioModules[] =
        legalarioConfig?.modules && legalarioConfig?.modules.length
          ? (legalarioConfig?.modules as LegalarioModules[])
          : ["documents", "signature"];

      legalario.signature({
        signerId: signer.id,
        signature_types: {
          draw: "disabled",
          efirma: "enabled",
          keyboard: "disabled",
        },
        callbacks: {},
        modules,
      });
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  useEffect(() => {
    const legal_presentatives: DocumentSigner[] = [];

    documents.forEach((document) => {
      if (document.signers_reference && document.signers_reference.length > 0) {
        document.signers_reference.forEach((signer) => {
          if (signer.person_type_code && PERSON_CODES_LEGAL_REPRESENTATIVE.includes(signer.person_type_code)) {
            if (signer.id) {
              const is_added = legal_presentatives.find((s) => s.id === signer.id);
              if (!is_added) {
                legal_presentatives.push(signer);
              }
            }
          }
        });
      }
    });

    let checklist_documents: CheckListSignatureDocument[] = [];

    if (checklistDocuments && checklistDocuments.length > 0) {
      const valid_documents = checklistDocuments.filter((item) => validateStructureCheckListSignatureDocument(item));
      if (valid_documents.length > 0) {
        checklist_documents = valid_documents;
      }
    }

    if (checklist_documents.length === 0) {
      if (application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
        checklist_documents = DEFAULT_CONFIG_PFAE;
      } else {
        checklist_documents = DEFAULT_CONFIG_PM;
      }
    }

    checklist_documents.sort((a, b) => (a.order || 0) - (b.order || 0));
    legal_presentatives.sort((a, b) => (a.sign_order || 0) - (b.sign_order || 0));

    setChecklistDocumentsToSign(checklist_documents);
    setLegalRepresentatives(legal_presentatives);
  }, [documents]);

  return (
    <Box>
      <Typography
        mt={4}
        textAlign={"center"}
        variant={isMobileOrTablet ? "body2" : "body1"}
        color={theme?.palette?.primary?.main}
        fontWeight={600}
      >
        Es momento de firmar digitalmente tu contrato, ¡El proceso es rápido y sencillo!
      </Typography>

      <Typography
        mt={isMobileOrTablet ? 2 : 1}
        textAlign={"center"}
        variant={isMobileOrTablet ? "body2" : "body1"}
        color={theme?.palette?.primary?.main}
        fontWeight={600}
      >
        Recuerda que los documentos se deben de firmar con la FIEL del Representante Legal y/o Apoderado Legal de la
        empresa
      </Typography>

      <Box
        mt={isMobileOrTablet ? 2 : 4}
        sx={{
          backgroundColor: theme?.palette?.components?.phoneInput?.main || theme?.palette?.primary?.base,
          borderRadius: "10px",
          padding: "20px",
          maxWidth: "98%",
        }}
      >
        <Typography
          mt={isMobileOrTablet ? 2 : 1}
          textAlign={"left"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
          fontWeight={600}
        >
          Se firmarán los siguientes documentos
        </Typography>

        <List sx={{ listStyleType: "disc", paddingLeft: 2 }}>
          {checklistDocumentsToSign.map((item) => (
            <ListDocuments key={item.order} title={item.title} documents={item.documents} showLegend={true} />
          ))}
        </List>
      </Box>

      <Box
        mt={isMobileOrTablet ? 2 : 4}
        sx={{
          backgroundColor: theme?.palette?.components?.phoneInput?.main || theme?.palette?.primary?.base,
          borderRadius: "10px",
          padding: "20px",
          maxWidth: "98%",
        }}
      >
        <Typography
          mt={isMobileOrTablet ? 2 : 1}
          textAlign={"left"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
          fontWeight={600}
        >
          Deberan firmar los siguientes representantes legales
        </Typography>

        {legalRepresentatives.map((signer, index) => (
          <React.Fragment key={signer.id}>
            <Box
              mt={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              color={theme?.palette?.primary?.dark}
            >
              <Box>
                <Typography
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  sx={{ flex: 1, textAlign: "left", fontWeight: 600 }}
                >
                  {signer.name}
                </Typography>
                {signer.phone && (
                  <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ flex: 1, textAlign: "left" }}>
                    <span style={{ fontWeight: 600 }}>Teléfono:</span> {formatPhoneNumber(signer.phone)}
                  </Typography>
                )}
              </Box>

              <ButtonFirma status={signer.status} onClick={() => handleSignLegalRepresentative(signer)} />
            </Box>
            {index < legalRepresentatives.length - 1 && (
              <Box width="100%" height="1px" bgcolor={theme?.palette?.divider} />
            )}
          </React.Fragment>
        ))}

        {legalRepresentatives.length === 0 && (
          <Box mt={2} display="flex" justifyContent="center" alignItems="center">
            <Typography
              mt={isMobileOrTablet ? 2 : 1}
              textAlign={"left"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              fontWeight={600}
            >
              No se han encontrado representantes legales para firmar
            </Typography>
          </Box>
        )}
      </Box>

      <Typography
        mt={2}
        textAlign={"center"}
        variant={isMobileOrTablet ? "body2" : "body1"}
        color={theme?.palette?.primary?.main}
        fontWeight={600}
      >
        En caso de que tengas alguna duda no dudes en contactar a tu asesor
      </Typography>

      <Grid container columnSpacing={1} my={4} justifyContent="center">
        <Grid item xs={6} sm={4} md={3}></Grid>
      </Grid>
    </Box>
  );
};

export default FirmaLegalario;
